import { useQuery } from '@tanstack/react-query';
import { validate as validateUUID } from 'uuid';

import { call } from '@spacefill/shared/src/utils/Backend';

import { useReloadPageAndClearStorageError } from '../utils/contextSwitch';

export function BreadcrumbViewLabel({ match }) {
    const { data, isLoading } = useQuery({
        queryKey: ['saved-filters', match.params.viewId],
        queryFn: () => call(`saved-filters/${match.params.viewId}`),
    });

    if (isLoading) {
        return;
    }

    const name = data?.name;
    if (name) {
        return <>{name}</>;
    } else {
        return <>{match.params.viewId}</>;
    }
}

export function BreadcrumbOrderLabel({ match }) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['orders', match.params.orderId],
        queryFn: () => call(`orders/${match.params.orderId}`),
        enabled: validateUUID(match.params.orderId),
    });

    useReloadPageAndClearStorageError(error);

    if (isLoading) {
        return;
    }
    if (data?.shipperOrderReference) {
        return <>{data?.shipperOrderReference}</>;
    } else {
        return <>{match.params.orderId}</>;
    }
}

export function BreadcrumbTransportLabel({ match }) {
    const orderId = match.params.orderId;

    const { data, isLoading } = useQuery({
        queryKey: [`orders/${orderId}/getTransport`, orderId],
        queryFn: async () => await call(`orders/${orderId}/getTransport`),
    });

    if (isLoading) {
        return;
    }
    if (data?.transportOrderReference) {
        return <>{data?.transportOrderReference}</>;
    } else {
        return <>{orderId}</>;
    }
}

export function BreadcrumCarrierLabel({ match }) {
    const carrierId = match.params.carrierId;

    const { data, isLoading } = useQuery({
        queryKey: [`customer-carriers/${carrierId}`, carrierId],
        queryFn: async () => await call(`customer-carriers/${carrierId}`),
    });

    if (isLoading) {
        return;
    }
    if (data?.carrier?.company) {
        return <>{data?.carrier.company}</>;
    } else {
        return <>{carrierId}</>;
    }
}

export function BreadcrumbAppointmentLabel({ match }) {
    const appointmentId = match.params.appointmentId;

    const { data, isLoading, error } = useQuery({
        queryKey: ['appointments', appointmentId],
        queryFn: () => call(`appointments/${appointmentId}`),
        enabled: !!appointmentId && appointmentId !== 'edit',
    });

    useReloadPageAndClearStorageError(error);

    if (match.params.appointmentId === 'edit') {
        return;
    }

    if (isLoading) {
        return;
    }

    const order = data?.transports?.[0]?.order || data?.transport?.order;
    const title = order?.shipperOrderReference || data?.extOrderReference;

    if (title) {
        return <>{title}</>;
    } else {
        return <>{appointmentId}</>;
    }
}

export function LoadableBreadcrumbItem({ loaded: loadedText, loading: loadingText }, bcType) {
    const [bcText, testId] = loadedText ? [loadedText, `${bcType}-loaded`] : [loadingText, `${bcType}-loading`];
    return <span data-testid={testId}>{bcText}</span>;
}
